import React from 'react';
import type { TableLayoutConfig } from '../../config/uiSizes';
import useBreakpoint from './useBreakpoint';

export interface TableLayoutReturn {
  /** Live column widths in px — feed directly into your <colgroup> */
  colWidths: number[];
  /** The full config object for the active breakpoint (includes class strings) */
  config: TableLayoutConfig;
  /** onMouseDown handler for each column resize handle — pass the column index */
  handleResizeMouseDown: (e: React.MouseEvent, colIndex: number) => void;
}

/**
 * Combines breakpoint detection, column width state, and drag-to-resize logic
 * into a single hook driven by a `TableLayoutConfig`.
 *
 * @example
 * const layout = useTableLayout(dashboardTableConfig);
 *
 * // In JSX:
 * <colgroup>
 *   {layout.colWidths.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}
 * </colgroup>
 * <th className={`... ${layout.config.headerTextSizeClass}`}>...</th>
 * <td className={`... ${layout.config.cellTextSizeClass}`}>...</td>
 * <div onMouseDown={(e) => layout.handleResizeMouseDown(e, 0)} />
 */
const useTableLayout = (config: TableLayoutConfig): TableLayoutReturn => {
  const bp = useBreakpoint();

  const [colWidths, setColWidths] = React.useState<number[]>(
    () => config.breakpoints[bp].colWidths
  );

  // Reset column widths when the breakpoint changes (e.g. user resizes window)
  React.useEffect(() => {
    setColWidths(config.breakpoints[bp].colWidths);
  }, [bp, config]);

  // Store latest colWidths in a ref so the resize closure always sees current values
  const colWidthsRef = React.useRef<number[]>(colWidths);
  React.useEffect(() => {
    colWidthsRef.current = colWidths;
  }, [colWidths]);

  const resizingCol = React.useRef<number | null>(null);
  const resizeStartX = React.useRef<number>(0);
  const resizeStartW = React.useRef<number>(0);

  const handleResizeMouseDown = React.useCallback(
    (e: React.MouseEvent, colIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      resizingCol.current = colIndex;
      resizeStartX.current = e.clientX;
      resizeStartW.current = colWidthsRef.current[colIndex] ?? 0;

      let hasDragged = false;

      const onMove = (ev: MouseEvent) => {
        if (resizingCol.current === null) return;
        hasDragged = true;
        const newW = Math.max(40, resizeStartW.current + ev.clientX - resizeStartX.current);
        setColWidths((prev) => {
          const next = [...prev];
          next[resizingCol.current!] = newW;
          return next;
        });
      };

      const onUp = () => {
        resizingCol.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (hasDragged) {
          // Discard the click that fires after mouseup so sort is not triggered
          const eatClick = (ev: MouseEvent) => {
            ev.stopPropagation();
            document.removeEventListener('click', eatClick, true);
          };
          document.addEventListener('click', eatClick, true);
        }
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [] // stable — closure reads widths via ref
  );

  return { colWidths, config, handleResizeMouseDown };
};

export default useTableLayout;
